/* ─────────────────────────────────────────────────────────
   Chefs – CRUD page
   • Filters by a real Chef role _id (CHEF_ROLE_ID below)
   • Uses its own DataTable but shares drawer / filter logic
────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal                    from 'sweetalert2';
import { useDispatch }         from 'react-redux';

import Breadcrumb              from '@/components/common/Breadcrumb';
import CustomDropdown          from '@/components/common/CustomDropdown';
import Card                    from '@/components/ui/cards/Card';
import CardHeader              from '@/components/ui/cards/CardHeader';
import PerPageSelection        from '@/components/ui/tables/PerPageSelection';
import PritntPdfTable          from '@/components/common/PritntPdfTable';
import PermissionCheck         from '@/components/common/PermissionCheck';

import validationErrors        from '@/lib/validationErrors';
import { cn }                  from '@/lib/utils';
import { getAdminService,
         deleteAdminService }  from '@/services/AdminServices';

/* components reused from the employee module */
import DataTableFilter         from './component/DataTableFilter';
import AddEditDrawer           from './component/AddEditDrawer';
/* chef-specific table (view / edit / delete buttons route to /chefs) */
import DataTable               from './component/DataTable';

/* REAL ObjectId of the “Chef” role created by your seeder ---------------- */
const CHEF_ROLE_ID = '685b1af7956beac77bebe6d0';

const ChefsPage = ({ pageTitle = 'Chefs' }) => {
  const dispatch = useDispatch();
  const { printTableRef, handleDownloadPdf } = PritntPdfTable('Chefs Data');

  /* ───────── state ───────── */
  const [dataSource,   setDataSource]   = useState([]);
  const [totalRecord,  setTotalRecord]  = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(false);
  const [editDataId,   setEditDataId]   = useState('');
  const [isLoading,    setIsLoading]    = useState(false);
  const [filterOpen,   setFilterOpen]   = useState(false);
  const filterDivRef   = useRef(null);

  const [dataFilter, setDataFilter] = useState({
    search : '',
    page   : 1,
    limit  : 10,
    extra_filter: { role_id: CHEF_ROLE_ID },
  });

  const columns = [
    { name: 'Name',   isIncludePrint: true },
    { name: 'Email',  isIncludePrint: true },
    { name: 'Phone',  isIncludePrint: true },
    { name: 'Role',   isIncludePrint: true },
    { name: 'Status', isIncludePrint: true },
    { name: 'Salary', isIncludePrint: true },
    { name: 'Action', isIncludePrint: false },
  ];

  /* ───────── data fetcher ───────── */
  const fetchTableData = useCallback(() => {
    setIsLoading(true);
    dispatch(getAdminService(dataFilter))
      .then((res) => {
        const { data, total } = res.data.data;
        setDataSource(data);
        setTotalRecord(total);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  }, [dispatch, dataFilter]);

  /* ───────── event handlers ───────── */
  const handleDrawerOpen = (id = '') => {
    setEditDataId(id);
    setActiveDrawer(true);
    document.body.style.overflowY = 'hidden';
  };

  const handleDrawerClose = () => {
    setActiveDrawer(false);
    setEditDataId('');
    document.body.style.overflowY = '';
  };

  const onChangeFilter = (key, value) => {
    if (key === 'extra_filter') {
      setDataFilter((prev) => ({
        ...prev,
        extra_filter: { ...prev.extra_filter, ...value },
      }));
    } else {
      setDataFilter((prev) => ({ ...prev, [key]: value }));
    }
  };

  const toggleFilter = () => {
    if (!filterDivRef.current) return;
    setFilterOpen((o) => !o);
    filterDivRef.current.style.height = !filterOpen ? '100%' : '0px';
  };

  const deleteRecord = (id) => {
    Swal.fire({
      title: 'Delete this chef?',
      text : 'This action is irreversible.',
      icon : 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
    }).then((result) => {
      if (!result.isConfirmed) return;

      setIsLoading(true);
      dispatch(deleteAdminService(id))
        .then(() => {
          Swal.fire('Deleted!', 'Chef removed.', 'success');
          fetchTableData();
        })
        .catch(({ message, errorData }) => validationErrors(errorData, message))
        .finally(() => setIsLoading(false));
    });
  };

  /* first load + whenever filters change */
  useEffect(() => fetchTableData(), [fetchTableData]);

  /* ───────── render ───────── */
  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />

      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>

          <div className="db-card-filter">
            <div className="db-field-down-arrow">
              <PerPageSelection
                onChangeParPage={onChangeFilter}
                limit={dataFilter.limit}
              />
            </div>

            <button
              className="db-card-filter-btn table-filter-btn"
              onClick={toggleFilter}
            >
              <i className="lab lab-filter lab-font-size-14" />
              <span>Filter</span>
            </button>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[{
                icon: 'lab-printer-line',
                text: 'Print',
                dropDownAction: handleDownloadPdf,
              }]}
            />

            <PermissionCheck permsission="create_employees">
              <button
                type="button"
                onClick={() => handleDrawerOpen('')}
                className="db-btn h-[37px] text-white bg-primary"
              >
                <i className="lab lab-add-circle-line" />
                Add Chef
              </button>
            </PermissionCheck>
          </div>
        </CardHeader>

        {/* Filter accordion */}
        <div
          className={cn('table-filter-div', filterOpen && 'active')}
          ref={filterDivRef}
          style={{ height: filterOpen ? '100%' : '0px' }}
        >
          <DataTableFilter onChangeFilter={onChangeFilter} />
        </div>

        {/* Data table */}
        <DataTable
          isLoading     ={isLoading}
          tableColumn   ={columns}
          dataSource    ={dataSource}
          totalRecord   ={totalRecord}
          dataFilter    ={dataFilter}
          onChangePage  ={onChangeFilter}
          handleDrawerOpen={handleDrawerOpen}
          deleteRecord  ={deleteRecord}
        />

        {/* Drawer */}
        <div ref={printTableRef}>
          <AddEditDrawer
            titleText        ={pageTitle}
            getTableData     ={fetchTableData}
            activeDrawer     ={activeDrawer}
            setActiveDrawer  ={handleDrawerClose}
            editDataId       ={editDataId}
          />
        </div>
      </Card>
    </>
  );
};

export default ChefsPage;
